import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { usePurchaseOrder, type PurchaseOrderLineItem } from '@/hooks';

interface PurchaseOrderReceiveDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseOrderId: string | null;
  orderNumber?: string;
  onConfirm: (lines: { lineId: string; quantityReceived: number }[]) => void;
  submitting?: boolean;
}

/**
 * Receiving dialog for a purchase order. Shows, per line, whether the part
 * lands in main stock or in the transit warehouse (work-order-linked), the
 * already-received quantity, and lets the user receive a partial amount.
 * Receiving more than the remaining outstanding quantity is prevented.
 */
export function PurchaseOrderReceiveDialog({
  open,
  onClose,
  purchaseOrderId,
  orderNumber,
  onConfirm,
  submitting,
}: PurchaseOrderReceiveDialogProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');

  const detailQuery = usePurchaseOrder(open ? purchaseOrderId ?? undefined : undefined);
  const detail = detailQuery.data;

  // Map lineId -> quantity to receive in this session
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const openLines = useMemo<PurchaseOrderLineItem[]>(
    () => (detail?.items ?? []).filter((l) => l.quantityReceived < l.quantity),
    [detail],
  );

  // Default each open line to its remaining quantity when data loads.
  useEffect(() => {
    if (!detail) return;
    const next: Record<string, number> = {};
    for (const line of detail.items) {
      const remaining = line.quantity - line.quantityReceived;
      if (remaining > 0) next[line.id] = remaining;
    }
    setQuantities(next);
  }, [detail]);

  const remainingFor = (line: PurchaseOrderLineItem) => line.quantity - line.quantityReceived;

  const setLineQty = (line: PurchaseOrderLineItem, value: number) => {
    const remaining = remainingFor(line);
    const clamped = Math.max(0, Math.min(remaining, Math.floor(value) || 0));
    setQuantities((prev) => ({ ...prev, [line.id]: clamped }));
  };

  const handleReceiveAll = () => {
    const next: Record<string, number> = {};
    for (const line of openLines) next[line.id] = remainingFor(line);
    setQuantities(next);
  };

  const payloadLines = useMemo(
    () =>
      openLines
        .map((line) => ({ lineId: line.id, quantityReceived: quantities[line.id] ?? 0 }))
        .filter((l) => l.quantityReceived > 0),
    [openLines, quantities],
  );

  const canSubmit = payloadLines.length > 0 && !submitting;

  const workOrderLabel = (line: PurchaseOrderLineItem): string | null => {
    const wo = line.linkedWorkOrder;
    if (!wo) return null;
    return [wo.vehicleRegistration, wo.customerName].filter(Boolean).join(' · ') || null;
  };

  return (
    <ResponsiveDialog
      mobileTitle={t('purchaseOrderReceive.title', { orderNumber: orderNumber ?? '' })}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogHeader onClose={onClose}>
        <Typography variant="h6">
          {t('purchaseOrderReceive.title', { orderNumber: orderNumber ?? detail?.orderNumber ?? '' })}
        </Typography>
      </DialogHeader>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('purchaseOrderReceive.intro')}
        </Typography>

        {detailQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : detailQuery.isError ? (
          <Alert severity="error">{t('purchaseOrderReceive.loadError')}</Alert>
        ) : openLines.length === 0 ? (
          <Alert severity="info">{t('purchaseOrderReceive.nothingToReceive')}</Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button size="small" onClick={handleReceiveAll} disabled={submitting}>
                {t('purchaseOrderReceive.receiveAll')}
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('purchaseOrderReceive.colPart')}</TableCell>
                    <TableCell>{t('purchaseOrderReceive.colDestination')}</TableCell>
                    <TableCell align="right">{t('purchaseOrderReceive.colOrdered')}</TableCell>
                    <TableCell align="right">{t('purchaseOrderReceive.colAlreadyReceived')}</TableCell>
                    <TableCell align="right">{t('purchaseOrderReceive.colReceiveNow')}</TableCell>
                    <TableCell>{t('purchaseOrderReceive.colRemaining')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openLines.map((line) => {
                    const remaining = remainingFor(line);
                    const receiveNow = quantities[line.id] ?? 0;
                    const remainingAfter = remaining - receiveNow;
                    const isTransit = !!line.linkedBookingId;
                    const woLabel = workOrderLabel(line);
                    return (
                      <TableRow key={line.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {line.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                            {line.partNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            icon={isTransit ? <LocalShippingIcon /> : <WarehouseIcon />}
                            color={isTransit ? 'warning' : 'default'}
                            label={
                              isTransit
                                ? t('purchaseOrderReceive.destinationTransit')
                                : t('purchaseOrderReceive.destinationStock')
                            }
                          />
                          {isTransit && woLabel && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {t('purchaseOrderReceive.workOrderFor', { label: woLabel })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{line.quantity}</TableCell>
                        <TableCell align="right">{line.quantityReceived}</TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            type="number"
                            value={receiveNow}
                            onChange={(e) => setLineQty(line, parseFloat(e.target.value))}
                            inputProps={{ min: 0, max: remaining, step: 1 }}
                            sx={{ width: 90 }}
                            disabled={submitting}
                          />
                        </TableCell>
                        <TableCell>
                          {remainingAfter <= 0 ? (
                            <Chip size="small" color="success" label={t('purchaseOrderReceive.fullyReceived')} />
                          ) : (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t('purchaseOrderReceive.partialOpen', { remaining: remainingAfter })}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(payloadLines)}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('purchaseOrderReceive.confirmReceive')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
